import { z } from 'zod';
import { timestampSchema } from './admin.js';
import { visibilitySchema, type ProjectPermission, type Visibility } from './permissions.js';
import { personSchema } from './projects.js';

/** COL-03: attachments on tasks with visibility (like comments, COL-02). */

export const attachmentProblemTypes = {
  tooLarge: 'urn:pm-tool:problem:attachment-too-large',
  typeNotAllowed: 'urn:pm-tool:problem:attachment-type-not-allowed',
  /** `complete` without a matching upload (missing, other size or type, wrong content). */
  uploadInvalid: 'urn:pm-tool:problem:attachment-upload-invalid',
} as const;

type FileType = {
  mimeType: string;
  /** First bytes every valid file of this type starts with (checked after the upload). */
  magic?: { offset: number; bytes: readonly number[] }[];
  /** Shown inline as image preview. Raster images only – never SVG or HTML. */
  preview?: boolean;
};

const ascii = (text: string) => Array.from({ length: text.length }, (_, i) => text.charCodeAt(i));
const zip = [{ offset: 0, bytes: [0x50, 0x4b, 0x03, 0x04] }];
const ole = [{ offset: 0, bytes: [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1] }];

/**
 * Allowed file types by extension. The server derives the type from the extension and never
 * trusts the browser's MIME type; downloads are `attachment` except image previews.
 */
export const attachmentFileTypes: Record<string, FileType> = {
  png: {
    mimeType: 'image/png',
    magic: [{ offset: 0, bytes: [0x89, 0x50, 0x4e, 0x47] }],
    preview: true,
  },
  jpg: { mimeType: 'image/jpeg', magic: [{ offset: 0, bytes: [0xff, 0xd8, 0xff] }], preview: true },
  jpeg: {
    mimeType: 'image/jpeg',
    magic: [{ offset: 0, bytes: [0xff, 0xd8, 0xff] }],
    preview: true,
  },
  gif: { mimeType: 'image/gif', magic: [{ offset: 0, bytes: ascii('GIF8') }], preview: true },
  webp: {
    mimeType: 'image/webp',
    magic: [
      { offset: 0, bytes: ascii('RIFF') },
      { offset: 8, bytes: ascii('WEBP') },
    ],
    preview: true,
  },
  pdf: { mimeType: 'application/pdf', magic: [{ offset: 0, bytes: ascii('%PDF') }] },
  txt: { mimeType: 'text/plain' },
  csv: { mimeType: 'text/csv' },
  docx: {
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    magic: zip,
  },
  xlsx: {
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    magic: zip,
  },
  pptx: {
    mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    magic: zip,
  },
  doc: { mimeType: 'application/msword', magic: ole },
  xls: { mimeType: 'application/vnd.ms-excel', magic: ole },
  ppt: { mimeType: 'application/vnd.ms-powerpoint', magic: ole },
  odt: { mimeType: 'application/vnd.oasis.opendocument.text', magic: zip },
  ods: { mimeType: 'application/vnd.oasis.opendocument.spreadsheet', magic: zip },
  odp: { mimeType: 'application/vnd.oasis.opendocument.presentation', magic: zip },
  zip: { mimeType: 'application/zip', magic: zip },
  eml: { mimeType: 'message/rfc822' },
  msg: { mimeType: 'application/vnd.ms-outlook', magic: ole },
};

/** Bytes to read for the magic check (the longest offset + length above). */
export const ATTACHMENT_MAGIC_BYTES = 16;

export function attachmentExtension(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot <= 0 ? '' : fileName.slice(dot + 1).toLowerCase();
}

export function fileTypeFor(fileName: string): FileType | undefined {
  const extension = attachmentExtension(fileName);
  return Object.hasOwn(attachmentFileTypes, extension) ? attachmentFileTypes[extension] : undefined;
}

/** Whether the start of the file matches the type's signature (types without one always do). */
export function matchesMagic(type: FileType, start: Uint8Array): boolean {
  return (type.magic ?? []).every(({ offset, bytes }) =>
    bytes.every((byte, i) => start[offset + i] === byte),
  );
}

/**
 * Display name without paths and control characters, at most 200 characters (keeping the
 * extension).
 */
export function sanitizeFileName(name: string): string {
  const base = name.split(/[/\\]/).pop() ?? '';
  // eslint-disable-next-line no-control-regex -- control characters are exactly what is removed
  const clean = base.replace(/[\u0000-\u001f\u007f]/g, '').trim();
  if (clean.length <= 200) return clean;
  const extension = attachmentExtension(clean);
  const keep = extension === '' ? 200 : 199 - extension.length;
  return extension === '' ? clean.slice(0, keep) : `${clean.slice(0, keep)}.${extension}`;
}

/** Internal attachments need `attachment.internal` (never held by externals, PERM-04). */
export function canSeeAttachment(
  permissions: ReadonlySet<ProjectPermission>,
  visibility: Visibility,
): boolean {
  return visibility === 'external' || permissions.has('attachment.internal');
}

/** Uploading: internal ones need `attachment.internal`, external ones `attachment.upload`. */
export function canUploadAttachment(
  permissions: ReadonlySet<ProjectPermission>,
  visibility: Visibility,
): boolean {
  return permissions.has(visibility === 'internal' ? 'attachment.internal' : 'attachment.upload');
}

/** Like comments: internal by default for users who may upload internal attachments. */
export function defaultAttachmentVisibility(
  permissions: ReadonlySet<ProjectPermission>,
): Visibility | null {
  if (permissions.has('attachment.internal')) return 'internal';
  return permissions.has('attachment.upload') ? 'external' : null;
}

export const attachmentSchema = z.object({
  id: z.uuid(),
  taskId: z.uuid(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  visibility: visibilitySchema,
  uploadedBy: personSchema.nullable(),
  createdAt: timestampSchema,
  /** Raster image that may be shown inline (download?inline=true). */
  previewable: z.boolean(),
  /** Own attachment that may still be removed (not in archived projects). */
  canDelete: z.boolean(),
});
export type Attachment = z.infer<typeof attachmentSchema>;

export const attachmentIdParamsSchema = z.object({ attachmentId: z.uuid() });

export const createAttachmentBodySchema = z.object({
  fileName: z.string().trim().min(1).max(500),
  sizeBytes: z.number().int().min(1),
  /** Default: internal for users with attachment.internal (like comments); externals: external. */
  visibility: visibilitySchema.optional(),
});
export type CreateAttachmentBody = z.infer<typeof createAttachmentBodySchema>;

/** Pending attachment plus a presigned URL for exactly one PUT with these headers. */
export const createAttachmentResponseSchema = z.object({
  attachment: attachmentSchema,
  upload: z.object({
    url: z.string(),
    method: z.literal('PUT'),
    headers: z.record(z.string(), z.string()),
    expiresInSeconds: z.number().int(),
  }),
});
export type CreateAttachmentResponse = z.infer<typeof createAttachmentResponseSchema>;

export const attachmentListResponseSchema = z.object({ items: z.array(attachmentSchema) });
export type AttachmentListResponse = z.infer<typeof attachmentListResponseSchema>;

export const attachmentDownloadQuerySchema = z.object({
  /** Image preview: inline instead of a download (raster images only). */
  inline: z.stringbool().optional(),
});
